import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import { ContactUsMode } from '../components/forms/ContactForm';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle, StrapiApiPlugin } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import { isNonEmptyArray } from '../utils';
import AutomationProtocolsWidget from '../widgets/interactive/AutomationProtocolsWidget';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import { FeatureShowcase } from '../widgets/showcases/FeatureShowcase/FeatureShowcase';

// Dynamically import with SSR disabled
const LearnMoreShowcase = dynamic(() => import('../widgets/showcases/LearnMoreShowcase'), { ssr: false });

type IntegrationsPageStaticProps = {
  plugins: StrapiApiPlugin[];
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<IntegrationsPageStaticProps> = async (context) => {
  const plugins = await StrapiApiClient.getAllPlugins();
  const articles = await StrapiApiClient.getRandomMostRecentArticles();
  const props: IntegrationsPageStaticProps = { plugins, articles };
  return withGlobalDefaultStaticProps(context, { props });
};

const IntegrationsPage: NextPageWithLayout<IntegrationsPageStaticProps> = (props) => {
  const { plugins, articles } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Integrations);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Integrations"
        title="Explore Gaspar AI integrations"
        description="Transform every application with generative AI by seamlessly integrating Gaspar AI with your daily-use tools."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
        destinationButtonLabel="See it in action"
      />
      <PageSection>
        <PageSectionTitle text="Discover in detail" />
        {isNonEmptyArray(plugins) && <AutomationProtocolsWidget plugins={plugins} />}
      </PageSection>
      <PageSection type={PageSectionType.BlueElectric}>
        <FeatureShowcase
          title="Connect seamlessly with a single click"
          description="Connect Gaspar AI to your most used apps to boost productivity and drive ROI. Our platform allows you to integrate easily and select only the apps you want."
          panels={[{ imageUrl: CDNAssets.getUrl('integrations-page-connect-seamlessly') }]}
        />
      </PageSection>
      <PageSection>
        <LearnMoreShowcase articles={articles} />
      </PageSection>
      <PageSection isNarrowTop={true}>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
    </>
  );
};
IntegrationsPage.displayName = 'IntegrationsPage';
export default IntegrationsPage;
